import React, { useState, useEffect, useCallback } from 'react';
import './SearchDropdown.css';

interface SearchDropdownProps {
  options: string[];
  placeholder: string;
  onSelect: (item: string) => void;
}

const SearchDropdown: React.FC<SearchDropdownProps> = ({ options, placeholder, onSelect }) => {
  const [inputValue, setInputValue] = useState<string>('');
  const [filteredOptions, setFilteredOptions] = useState<string[]>([]);
  const [activeIndex, setActiveIndex] = useState<number>(-1);
  const inputRef = React.useRef<HTMLInputElement>(null);

  // Utility to update the input value and keep filtered list in sync
  const updateInputValue = useCallback(
    (value: string) => {
      setInputValue(value);
      if (value) {
        const filtered = options.filter((option) =>
          option.toLowerCase().includes(value.toLowerCase())
        );
        setFilteredOptions(filtered);
      } else {
        setFilteredOptions([]);
      }
      setActiveIndex(-1);
    },
    [options]
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value;
    // Only allow alphabetic characters and spaces
    const value = rawValue.replace(/[^a-zA-Z ]/g, '');
    setInputValue(value);
    if (value) {
      const filtered = options
        .filter((option) => option.toLowerCase().includes(value.toLowerCase()));
      setFilteredOptions(filtered);
    } else {
      setFilteredOptions([]);
    }
    setActiveIndex(-1);
  };

  const handleSelectOption = useCallback((option: string) => {
    onSelect(option);
    setInputValue('');
    setFilteredOptions([]);
    setActiveIndex(-1);
  }, [onSelect]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // If user types an alphabetical character or space while not focused on an
      // editable element, funnel that character into the search input.
      const isEditableElement =
        (document.activeElement &&
          ((document.activeElement.tagName === 'INPUT' &&
            (document.activeElement as HTMLInputElement).type === 'text') ||
            document.activeElement.tagName === 'TEXTAREA' ||
            (document.activeElement as HTMLElement).isContentEditable)) ||
        false;

      // Single-character keys (length === 1) represent printable characters.
      if (
        !isEditableElement &&
        e.key.length === 1 &&
        /^[a-zA-Z ]$/.test(e.key)
      ) {
        e.preventDefault();
        updateInputValue(inputValue + e.key);
        // Bring focus to the input to give visual feedback and allow further typing
        inputRef.current?.focus();
        return;
      }

      // Navigation only makes sense if there are filtered options to move through.
      if (filteredOptions.length === 0) return;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIndex((prevIndex) =>
          prevIndex < filteredOptions.length - 1 ? prevIndex + 1 : prevIndex
        );
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIndex((prevIndex) => (prevIndex > 0 ? prevIndex - 1 : prevIndex));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (filteredOptions.length === 1) {
          // Only one possible match – select it automatically
          handleSelectOption(filteredOptions[0]);
          return;
        }
        if (activeIndex !== -1 && filteredOptions[activeIndex]) {
          handleSelectOption(filteredOptions[activeIndex]);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [activeIndex, filteredOptions, handleSelectOption, inputValue, updateInputValue]);

  const currentPage = activeIndex === -1 ? 0 : Math.floor(activeIndex / 5);
  const displayedOptions = filteredOptions.slice(currentPage * 5, currentPage * 5 + 5);

  return (
    <div className="search-dropdown-container">
      <input
        ref={inputRef}
        type="text"
        value={inputValue}
        onChange={handleInputChange}
        placeholder={placeholder}
        className="search-input"
      />
      {displayedOptions.map((option, index) => {
        const actualIndex = currentPage * 5 + index;
        return (
          <div
            key={option}
            className={`option-cell ${actualIndex === activeIndex ? 'active' : ''}`}
            onClick={() => handleSelectOption(option)}
          >
            {option}
          </div>
        );
      })}
      {[...Array(Math.max(0, 5 - displayedOptions.length))].map((_, i) => (
        <div key={`empty-${i}`} className="option-cell empty" />
      ))}
    </div>
  );
};

export default SearchDropdown; 